import React, { useContext, useEffect } from "react";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import moment from "moment";
import {
  currencyFormat,
  getDisplayProduct,
  getSealDetailsDisplay,
  getSellerPayoutBreakdown,
} from "../../utils/utils";
import { OrderUserType } from "../../../constants/order";
import { NotificationType, SERVER_URL } from "../../../constants/global";
import { getHistoryStatusDisplay, StatusLabelChip } from "../../../routes/historyGrid";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import fedexGroundLogo from "../../Layout/AuthLayout/fedex-ground.svg";
import fedexExpressLogo from "../../Layout/AuthLayout/fedex-express.png";
import { useQueryUniqueUsers } from "../../../queries/pharmacy/hooks/useQueryUniqueUsers";
import { useQueryUser } from "../../../queries/user/hooks/useQueryUser";
import { ToasterContext } from "../Toaster/ToasterContext";
import axios from "axios";

const outlineActionSx = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: 13,
  color: "#334155",
  bgcolor: "#fff",
  borderColor: "#e2e8f0",
  borderRadius: "8px",
  height: 40,
  width: "100%",
  minWidth: 0,
  px: 1.25,
  justifyContent: "center",
  "&:hover": { borderColor: "#e2e8f0", bgcolor: "#f8fafc" },
} as const;

const invoiceActionSx = {
  ...outlineActionSx,
  color: "#fff",
  bgcolor: "#2563eb",
  borderColor: "#2563eb",
  "& .MuiButton-startIcon": { color: "#fff" },
  "&:hover": { bgcolor: "#1d4ed8", borderColor: "#1d4ed8" },
  "&.Mui-disabled": {
    color: "#94A3B8",
    bgcolor: "#F8FAFC",
    borderColor: "#E2E8F0",
    "& .MuiButton-startIcon": { color: "#94A3B8" },
  },
} as const;

interface ActionRequiredOrdersProps {
  orders: any;
  setSelectedOrders: (orders: any) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  user?: OrderUserType.buyer | OrderUserType.seller;
}

export const HistoryDialog: React.FunctionComponent<
  ActionRequiredOrdersProps
> = ({ orders, setSelectedOrders, open, setOpen, user }) => {
  const { addToast } = useContext(ToasterContext);
  const { data } = useQueryUser();
  const { data: pharmacyUsersData } = useQueryUniqueUsers({
    variables: { where: { id: +data?.me?.id } },
    skip: !data,
  });
  const isAdmin = pharmacyUsersData?.findUniqueUser.UserRoles[0]?.Role?.key === "admin";


  const [stripeTransactionID, setStripeTransactionID] = React.useState<string>("");
  const [stripeTransferID, setStripeTransferID] = React.useState<string>("");
  const [stripePayoutID, setStripePayoutID] = React.useState<string>("");
  const [StripePayoutAmount, setStripePayoutAmount] = React.useState<string>("");
  useEffect(() => {
    if (!isAdmin || !orders) return;
    const orderNumber = orders[0]?.orderNumber;
    const api = `${process.env.REACT_APP_API_URL}/v2/order/get/orderByOrderNumber?orderNumber=${orderNumber}`;
    axios.get(api)
      .then((response) => {
        setStripeTransactionID(response.data.data[0].stripeTransactionId || "pending");
        setStripeTransferID(response.data.data[0].stripeTransferId || "pending");
        setStripePayoutID(response.data.data[0].stripePayoutId || "pending");
        setStripePayoutAmount(response.data.data[0].stripePayoutAmount || "pending");
      })
      .catch((error) => {
        console.error("Error fetching order details:", error);
      });

  }, [isAdmin, orders]);



  const filteredOrders = orders.filter((order: any) => order.status === 1);

  const cutoffDateForGround = new Date("Mon Feb 20 2025 23:59:59 GMT+0400");

  const getTotalShippingFee = (orders: any) => {
    const filteredOrders = orders.filter((order: any) => order.status === 1);

    return filteredOrders
      .reduce((totalShippingFee: number, item: any) => {
        const orderDate = new Date(item.createdAt);
        const isNewOrderForGround = orderDate > cutoffDateForGround;
        const isFedexGround = item.ShippingMethod?.key === "fedex-ground";
        const isShippingPaidBySeller = Boolean(item.isShippingPaidBySeller);

        if (user === OrderUserType.seller) {
          // Subtract shipping fee when seller pays shipping
          return (
            totalShippingFee +
            ((isShippingPaidBySeller || (isNewOrderForGround && isFedexGround))
              ? parseFloat(item.shippingFee || "0")
              : 0)
          );
        } else {
          // Buyer sees free shipping when seller pays it
          return (
            totalShippingFee +
            (!(isShippingPaidBySeller || (isNewOrderForGround && isFedexGround))
              ? parseFloat(item.shippingFee || "0")
              : 0)
          );
        }
      }, 0)
      .toFixed(2);
  };

  const getTotalAmount = (orders: any) => {
    const filteredOrders = orders.filter((order: any) => order.status === 1);

    let total = 0;
    filteredOrders.forEach((item: any) => {
      total = total + Number(item.totalAmount);
    });

    return `${total.toFixed(2)}`;
  };

  const sellerPayoutBreakdown =
    user === OrderUserType.seller
      ? getSellerPayoutBreakdown(filteredOrders)
      : null;

  const closeDialog = () => {
    setOpen(false);
    setSelectedOrders(null);
  };

  const primaryOrder = orders?.[0];
  const orderNumber = primaryOrder?.orderNumber || "";
  const trackingNumber = primaryOrder?.trackingNumber;
  const labelPreparing =
    primaryOrder?.status === 1 &&
    primaryOrder?.shippingStatus === 1 &&
    !trackingNumber &&
    !(primaryOrder?.Label && primaryOrder?.Label.id);
  const canPrintChecklist =
    primaryOrder?.shippingStatus > 0 && Boolean(orderNumber) && !labelPreparing;
  const canDownloadLabel =
    user === OrderUserType.seller && primaryOrder?.shippingStatus > 0 && Boolean(trackingNumber);
  const invoiceUrl = (() => {
    const stripeId = filteredOrders[0]?.stripeTransactionId;
    if (!stripeId || primaryOrder?.status !== 1) return null;
    if (user === OrderUserType.seller && primaryOrder?.shippingStatus === 5) {
      return `${SERVER_URL}/pdf/invoice/seller/${stripeId}`;
    }
    if (user === OrderUserType.buyer && primaryOrder?.shippingStatus !== 0) {
      return `${SERVER_URL}/pdf/invoice/buyer/${stripeId}`;
    }
    return null;
  })();

  const statusDisplay = getHistoryStatusDisplay(primaryOrder);

  const handlePrintChecklist = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!canPrintChecklist) return;
    const checklistUrl = `${SERVER_URL}/file/order-checklist-${orderNumber}.pdf`;
    axios
      .head(checklistUrl)
      .then(() => {
        window.open(checklistUrl, "_blank");
      })
      .catch(() => {
        addToast({ type: NotificationType.Error, title: "Checklist not found. Please regenerate it." });
      });
  };

  const getPackTypeDetails = (type?: number, packaging?: string | number) => {
    if (type === 2) {
      return packaging !== undefined && packaging !== null && `${packaging}` !== ""
        ? `Partial (count: ${packaging})`
        : "Partial";
    }

    return "Full";
  };

  const renderPackageCondition = (
    packageCondition: number,
    sealDetails?: string,
    type?: number,
    packaging?: string | number
  ) => {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <Box sx={{ fontSize: 13, fontWeight: 500, color: "#0f172a", wordBreak: "break-word" }}>
          {packageCondition === 1 ? "Sealed" : "Non-Sealed"} | {getPackTypeDetails(type, packaging)}
        </Box>
        {sealDetails && (
          <Box sx={{ fontSize: 11, color: "#64748b", mt: 0.5, wordBreak: "break-word" }}>
            {getSealDetailsDisplay(sealDetails)}
          </Box>
        )}
      </Box>
    );
  };

  const summaryRow = (label: string, value: string, emphasize = false) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 2,
        mb: emphasize ? 0 : 1,
        fontSize: emphasize ? 16 : 14,
        fontWeight: emphasize ? 700 : 500,
        color: emphasize ? "#1d4ed8" : "#0f172a",
      }}
    >
      <Box sx={{ color: emphasize ? "#0f172a" : "#64748b", fontWeight: emphasize ? 700 : 500 }}>{label}</Box>
      <Box>{value}</Box>
    </Box>
  );

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={closeDialog}
      PaperProps={{ sx: { borderRadius: 2 } }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ fontSize: 20, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}>
            Order Details — #{orderNumber || "—"}
          </Box>
          <IconButton aria-label="Close" onClick={closeDialog} size="small" sx={{ color: "#64748b" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            p: 2,
            mb: 1.5,
            bgcolor: "#f8fafc",
            borderRadius: 2,
          }}
        >
          <Box sx={{ minWidth: 140, flex: "1 1 140px" }}>
            <Box sx={{ fontSize: 12, color: "#64748b", mb: 0.25 }}>Order Date</Box>
            <Box sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a", wordBreak: "break-word" }}>
              {primaryOrder?.createdAt ? moment(primaryOrder.createdAt).format("MMM D, YYYY") : "—"}
            </Box>
          </Box>
          <Box sx={{ minWidth: 160, flex: "1 1 180px" }}>
            <Box sx={{ fontSize: 12, color: "#64748b", mb: 0.25 }}>Tracking Number</Box>
            {trackingNumber ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0 }}>
                <Box
                  component="a"
                  href={`https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#2563eb",
                    textDecoration: "none",
                    wordBreak: "break-all",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {trackingNumber}
                </Box>
                <Tooltip title="Copy tracking number">
                  <IconButton
                    size="small"
                    aria-label="Copy tracking number"
                    onClick={() => navigator.clipboard.writeText(String(trackingNumber))}
                    sx={{ color: "#64748b" }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Box sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>—</Box>
            )}
          </Box>
          <Box sx={{ minWidth: 160, flex: "1 1 160px" }}>
            <Box sx={{ fontSize: 12, color: "#64748b", mb: 0.25 }}>Shipping Method</Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
              {primaryOrder?.ShippingMethod?.name && (
                <Box
                  component="img"
                  src={primaryOrder.ShippingMethod?.key === "fedex-ground" ? fedexGroundLogo : fedexExpressLogo}
                  alt=""
                  sx={{ height: 18, width: "auto", maxWidth: 88, objectFit: "contain", flexShrink: 0 }}
                />
              )}
              <Box sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a", wordBreak: "break-word" }}>
                {primaryOrder?.ShippingMethod?.name || "—"}
              </Box>
            </Box>
          </Box>
          <Box sx={{ minWidth: 140, flex: "1 1 140px" }}>
            <Box sx={{ fontSize: 12, color: "#64748b", mb: 0.25 }}>Status</Box>
            <StatusLabelChip
              label={statusDisplay.label}
              color={statusDisplay.color}
              bg={statusDisplay.bg}
              iconKey={statusDisplay.icon}
              fitContent
            />
          </Box>
        </Box>

        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2 }}>
          <Table size="small" sx={{ "& th, & td": { borderRight: "1px solid #e2e8f0", borderColor: "#e2e8f0", verticalAlign: "top" }, "& th:last-of-type, & td:last-of-type": { borderRight: "none" } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                {["Drug Name & Mfr", "NDC", "Form & Strength", "Lot & Exp Date", "Package Condition", "Qty", "Price / Unit", "Subtotal"].map((header) => {
                  const alignRight = header === "Price / Unit" || header === "Subtotal";
                  return (
                    <TableCell
                      key={header}
                      align={alignRight ? "right" : "left"}
                      sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#64748b", whiteSpace: "normal" }}
                    >
                      {header}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order: any) => {
                const displayProduct = getDisplayProduct(order);
                return (
                  <TableRow key={order.id}>
                    <TableCell sx={{ minWidth: 180 }}>
                      <Box sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a", lineHeight: 1.35, wordBreak: "break-word" }}>
                        {displayProduct.drugName}
                        {displayProduct.isFrozen && (
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.4,
                              ml: 0.75,
                              px: 0.75,
                              py: 0.1,
                              borderRadius: "999px",
                              bgcolor: "#e0f2fe",
                              color: "#0369a1",
                              border: "1px solid #bae6fd",
                              fontSize: 11,
                              fontWeight: 700,
                              verticalAlign: "middle",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <AcUnitIcon sx={{ fontSize: 12 }} />
                            Refrigerated
                          </Box>
                        )}
                      </Box>
                      {displayProduct.labelerName && (
                        <Box sx={{ mt: 0.25, fontSize: 12, color: "#64748b", wordBreak: "break-word" }}>
                          {displayProduct.labelerName}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: "#0f172a", wordBreak: "break-word" }}>{displayProduct.ndc}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: "#0f172a" }}>
                      <Box sx={{ wordBreak: "break-word" }}>{displayProduct.form || "—"}</Box>
                      <Box sx={{ color: "#64748b", wordBreak: "break-word" }}>{displayProduct.strength}</Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: "#0f172a" }}>
                      <Box sx={{ wordBreak: "break-word" }}>{displayProduct.lotNumber || "—"}</Box>
                      <Box sx={{ color: "#64748b" }}>{displayProduct.expDate ? moment(displayProduct.expDate).format("MM/YYYY") : "—"}</Box>
                    </TableCell>
                    <TableCell>
                      {renderPackageCondition(displayProduct.packageCondition, displayProduct.sealDetails, displayProduct.type, displayProduct.packaging)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: "#0f172a" }}>{order.quantity ?? 0}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, color: "#0f172a", whiteSpace: "nowrap" }}>
                      {currencyFormat(Number(displayProduct.price) || 0)}
                      <Box component="span" sx={{ color: "#64748b" }}> / {currencyFormat(Number(displayProduct.unitPrice) || 0)}</Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap" }}>
                      {currencyFormat(Number(order.totalAmount) || 0)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {primaryOrder?.status === 1 && isAdmin && (
          <Box sx={{ mt: 3 }}>
              <TableContainer component={Paper} elevation={0} sx={{ flex: "1 1 360px", maxWidth: 480, border: "1px solid #e2e8f0", borderRadius: 2 }}>
                <Table size="small" aria-label="admin transaction details">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ width: "40%", fontSize: 12, color: "#64748b" }}>Order Number</TableCell>
                      <TableCell sx={{ fontSize: 13, color: "#0f172a" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box>{orderNumber || "Unknown"}</Box>
                          <Tooltip title="Copy order number">
                            <IconButton size="small" onClick={() => navigator.clipboard.writeText(orderNumber || "")}>
                              <ContentCopyIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontSize: 12, color: "#64748b" }}>Transaction ID</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        <a href={`https://dashboard.stripe.com/payments/${stripeTransactionID}`} target="_blank" rel="noopener noreferrer">{stripeTransactionID}</a>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontSize: 12, color: "#64748b" }}>Transfer ID</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        <a href={`https://dashboard.stripe.com/connect/transfers/${stripeTransferID}`} target="_blank" rel="noopener noreferrer">{stripeTransferID}</a>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontSize: 12, color: "#64748b" }}>Stripe Payout ID</TableCell>
                      <TableCell sx={{ fontSize: 13, color: "#0f172a" }}>{stripePayoutID}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontSize: 12, color: "#64748b" }}>Stripe Payout Amount</TableCell>
                      <TableCell sx={{ fontSize: 13, color: "#0f172a" }}>{StripePayoutAmount}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
          </Box>
        )}
      </DialogContent>
      <Box
        sx={{
          flexShrink: 0,
          borderTop: "1px solid #e2e8f0",
          mt: 3,
          pt: 2,
          px: 2.5,
          pb: 2,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          bgcolor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 188px)",
            gap: 1,
            width: "fit-content",
            alignSelf: "flex-end",
            m: 0,
            "& .MuiButton-root": { m: 0 },
          }}
        >
          <Tooltip title="Print Checklist">
            <span style={{ display: "block" }}>
              <Button
                variant="outlined"
                disableElevation
                startIcon={<PrintOutlinedIcon />}
                disabled={!canPrintChecklist}
                onClick={handlePrintChecklist}
                sx={outlineActionSx}
              >
                Print Checklist
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Download Label">
            <span style={{ display: "block" }}>
              <Button
                variant="outlined"
                disableElevation
                startIcon={<PictureAsPdfOutlinedIcon />}
                disabled={!canDownloadLabel}
                onClick={() => {
                  if (!canDownloadLabel) return;
                  window.open(`${SERVER_URL}/file/order-shipping-label-${trackingNumber}.pdf`, "_blank");
                }}
                sx={outlineActionSx}
              >
                Download Label
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Track Package">
            <span style={{ display: "block" }}>
              <Button
                variant="outlined"
                disableElevation
                startIcon={<LocalShippingOutlinedIcon />}
                disabled={!trackingNumber}
                onClick={() => window.open(`https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`, "_blank")}
                sx={outlineActionSx}
              >
                Track Package
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="View Invoice">
            <span style={{ display: "block" }}>
              <Button
                variant="contained"
                disableElevation
                startIcon={<ReceiptOutlinedIcon />}
                disabled={!invoiceUrl}
                onClick={() => invoiceUrl && window.open(invoiceUrl, "_blank")}
                sx={invoiceActionSx}
              >
                View Invoice
              </Button>
            </span>
          </Tooltip>
        </Box>

        {primaryOrder?.status === 1 && (
            <Box sx={{ width: 320, maxWidth: "100%", ml: "auto", mt: 0, mb: 0, alignSelf: "flex-end", p: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 2, boxSizing: "border-box" }}>
              {user === OrderUserType.seller && sellerPayoutBreakdown && (
                <>
                  {summaryRow("Item(s) Total", currencyFormat(sellerPayoutBreakdown.itemsTotal))}
                  {sellerPayoutBreakdown.shippingFeeDeducted > 0 && summaryRow("Shipping Fee", `-${currencyFormat(sellerPayoutBreakdown.shippingFeeDeducted)}`)}
                  {sellerPayoutBreakdown.transactionFee > 0 && summaryRow("Transaction Fee", `-${currencyFormat(sellerPayoutBreakdown.transactionFee)}`)}
                  {summaryRow(`PrimePostRx Fee (${primaryOrder.primePostFee}%)`, `-${currencyFormat(sellerPayoutBreakdown.primePostFee)}`)}
                  {summaryRow(
                    "FedEx Insurance",
                    sellerPayoutBreakdown.fedExInsurance > 0
                      ? `-${currencyFormat(sellerPayoutBreakdown.fedExInsurance)}`
                      : currencyFormat(0)
                  )}
                  {sellerPayoutBreakdown.isCardPayment && summaryRow("Card Processing Fee", `-${currencyFormat(sellerPayoutBreakdown.stripeCardFee)}`)}
                  <Divider sx={{ borderColor: "#e2e8f0", my: 1.25 }} />
                  {summaryRow("Net Credit Total", currencyFormat(sellerPayoutBreakdown.creditTotal), true)}
                </>
              )}
              {user === OrderUserType.buyer && (
                <>
                  {summaryRow("Item(s) Total", currencyFormat(Number(getTotalAmount(orders))))}
                  {summaryRow("Item(s) Shipping", currencyFormat(Number(getTotalShippingFee(orders))))}
                  <Divider sx={{ borderColor: "#e2e8f0", my: 1.25 }} />
                  {summaryRow(
                    "Total",
                    currencyFormat(Number(getTotalAmount(orders)) + Number(getTotalShippingFee(orders))),
                    true
                  )}
                </>
              )}
            </Box>
        )}
      </Box>
    </Dialog>
  );
};
